import { type FocusEvent, type RefObject, useEffect, useRef, useState } from 'react'
import type { RemoveDictionaryWordOutcome } from '@/api/types/dictionary'
import { notifyDictionaryUpdateFailed } from '../handlers/notify-dictionary-change-failed'
import { customWordCount } from '../helpers/custom-word-count'

interface UseRemoveWordOptions {
  /** The words in the order they are listed, one remove button each. */
  sortedWords: readonly string[]
  onRemoveWord: (word: string) => Promise<RemoveDictionaryWordOutcome>
  /** Receives the screen-reader text for a word that was removed. */
  onAnnounce: (message: string) => void
  /** The word list, whose remove buttons are in the same order as `sortedWords`. */
  listRef: RefObject<HTMLUListElement | null>
  /** Takes focus when the last word is removed. */
  inputRef: RefObject<HTMLInputElement | null>
}

const NO_WORDS: ReadonlySet<string> = new Set()

/**
 * Removing a custom word from its row. The word stays listed until the backend has removed it, so a
 * failed remove leaves it visible and shows the update-failure toast. A row's button ignores
 * presses while its remove runs. When the removed word's button had focus, focus moves to the next
 * word's button, else the previous one, else the Add a word field, so a keyboard user can keep
 * going. The same happens when another user removes the word whose button has focus (ED-6).
 */
export function useRemoveWord({
  sortedWords,
  onRemoveWord,
  onAnnounce,
  listRef,
  inputRef,
}: UseRemoveWordOptions) {
  const [removingWords, setRemovingWords] = useState(NO_WORDS)
  // State lags a render behind, so a second press in the same tick reads this ref instead.
  const removingRef = useRef(new Set<string>())
  // Set before the remove is sent: the cache drops the word before the remove call returns.
  const pendingFocusRef = useRef<string | null>(null)
  // The word whose remove button has focus, so a remove by another user can move focus on too.
  const focusedWordRef = useRef<string | null>(null)
  // The list as it was, to find where a word that has gone used to be.
  const previousWordsRef = useRef(sortedWords)

  useEffect(() => {
    const previousWords = previousWordsRef.current
    const gone = [pendingFocusRef.current, focusedWordRef.current].find(
      (word): word is string => word !== null && !sortedWords.includes(word)
    )

    previousWordsRef.current = sortedWords

    if (!gone) return

    if (pendingFocusRef.current === gone) pendingFocusRef.current = null
    if (focusedWordRef.current === gone) focusedWordRef.current = null

    // The focused button unmounted with its row, which leaves focus on the body. Anywhere else, the
    // user has already moved on, so leave focus where it is.
    if (document.activeElement && document.activeElement !== document.body) return

    const buttons = listRef.current?.querySelectorAll<HTMLButtonElement>('button') ?? []
    const index = previousWords.indexOf(gone)
    // The word that took the removed word's place, else the one before it.
    const next = buttons[index] ?? buttons[index - 1] ?? inputRef.current

    next?.focus()
  }, [sortedWords, listRef, inputRef])

  const setRemoving = (word: string, isRemoving: boolean) => {
    if (isRemoving) removingRef.current.add(word)
    else removingRef.current.delete(word)

    setRemovingWords(new Set(removingRef.current))
  }

  const remove = async (word: string) => {
    if (removingRef.current.has(word)) return

    const remaining = sortedWords.filter((listed) => listed !== word).length

    pendingFocusRef.current = word
    setRemoving(word, true)

    let outcome: RemoveDictionaryWordOutcome

    try {
      outcome = await onRemoveWord(word)
    } finally {
      setRemoving(word, false)
    }

    if (outcome.status === 'removed') {
      onAnnounce(`${word} removed from dictionary. ${customWordCount(remaining)}.`)

      return
    }

    pendingFocusRef.current = null

    notifyDictionaryUpdateFailed()
  }

  // A row's remove button reports focus here (`onFocus` / `onBlur`).
  const trackFocus = (word: string) => {
    focusedWordRef.current = word
  }

  const untrackFocus = (event: FocusEvent<HTMLButtonElement>) => {
    // A button that left the page with its row has not lost focus to anything the user chose.
    if (event.currentTarget.isConnected) focusedWordRef.current = null
  }

  return { removingWords, remove, trackFocus, untrackFocus }
}
